import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminContext } from '../../contexts/AdminContextProvider';
import { resolveIcon } from '../../components/common/IconResolver';
import ViewShell from './ViewShell';
import type { ViewContext } from '../StubPage';

interface ExecOverviewProps {
  context?: ViewContext;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TenantStats {
  total_tenants: number;
  by_status: Record<string, number>;
  by_isolation_level: Record<string, number>;
  by_vertical: Record<string, number>;
}

interface HealthData {
  status: string;
  version: string;
  services: Record<string, string>;
}

interface KillSwitchStatus {
  active: boolean;
  activated_at?: string;
  reason?: string;
}

interface MetricCardProps {
  label: string;
  value: string | number;
  icon: string;
  status?: 'good' | 'warn' | 'bad';
  subtitle?: string;
}

// ---------------------------------------------------------------------------
// Metric Card
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, string> = {
  good: '#22c55e',
  warn: '#eab308',
  bad: '#ef4444',
};

const MetricCard: React.FC<MetricCardProps> = ({ label, value, icon, status = 'good', subtitle }) => (
  <div
    className="card stat-card card-elevated"
    style={{ minWidth: 0 }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3, 12px)' }}>
      <div
        style={{
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--accent-primary-muted, rgba(124, 58, 237, 0.1))',
          borderRadius: 'var(--radius-lg, 12px)',
          color: 'var(--accent-primary, #7c3aed)',
          flexShrink: 0,
        }}
      >
        {resolveIcon(icon, 24)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="stat-value" style={{ fontSize: 'var(--text-2xl, 24px)' }}>{value}</div>
        <div className="stat-label">{label}</div>
        {subtitle && (
          <div style={{ fontSize: '12px', color: STATUS_COLORS[status] || STATUS_COLORS.good, marginTop: '2px' }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const KPISkeleton: React.FC = () => (
  <div className="grid grid-cols-4" style={{ marginBottom: 'var(--space-6, 24px)' }}>
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="card stat-card card-elevated">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <div className="skeleton" style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-lg, 12px)' }} />
          <div>
            <div className="skeleton" style={{ width: '80px', height: '28px', marginBottom: '6px', borderRadius: 'var(--radius-sm, 4px)' }} />
            <div className="skeleton" style={{ width: '100px', height: '14px', borderRadius: 'var(--radius-sm, 4px)' }} />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Quick Action Button
// ---------------------------------------------------------------------------

interface QuickActionProps {
  label: string;
  icon: string;
  tab: string;
}

const QuickAction: React.FC<QuickActionProps> = ({ label, icon, tab }) => {
  // WQ-246: Dispatches admin-navigate event; Portal's listener calls
  // navigate(`/${tab}`) for the actual route change. Keeping the event
  // dispatch preserves test contracts and gives external dispatchers a
  // back-compat path.
  const handleClick = useCallback(() => {
    window.dispatchEvent(new CustomEvent('admin-navigate', { detail: { tab } }));
  }, [tab]);

  return (
    <button
      className="btn"
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 20px',
        background: 'var(--bg-secondary, rgba(255,255,255,0.05))',
        border: '1px solid var(--border-primary, rgba(255,255,255,0.1))',
        borderRadius: 'var(--radius-md, 8px)',
        color: 'var(--text-primary, #e2e8f0)',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 500,
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover, rgba(255,255,255,0.08))';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-primary, #7c3aed)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-secondary, rgba(255,255,255,0.05))';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-primary, rgba(255,255,255,0.1))';
      }}
    >
      {resolveIcon(icon, 18)}
      {label}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Service Status Dot
// ---------------------------------------------------------------------------

const StatusDot: React.FC<{ status: string; label: string }> = ({ status, label }) => {
  const color = status === 'healthy' ? '#22c55e' : status === 'degraded' ? '#eab308' : '#ef4444';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }} />
      <span style={{ fontSize: '14px', color: 'var(--text-primary, #e2e8f0)' }}>{label}</span>
      <span style={{ fontSize: '12px', color: 'var(--text-secondary, #94a3b8)', marginLeft: 'auto' }}>
        {status === 'healthy' ? 'Connected' : status === 'degraded' ? 'Degraded' : 'Disconnected'}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tenant Executive View (WQ-086: Tier 3 strategic variant)
// Shows strategic KPIs — no infrastructure metrics
// ---------------------------------------------------------------------------

const TenantExecView: React.FC<ExecOverviewProps> = ({ context }) => {
  const { activeTenant } = useAdminContext();
  const tenantName = activeTenant?.name || 'Organization';

  return (
    <ViewShell
      context={context}
      title="Executive Overview"
      icon="layout-dashboard"
      subtitle={`Strategic dashboard for ${tenantName}`}
    >
      {/* Strategic KPI Row — stubbed, real data from Phase 4.6.3+ */}
      <div className="grid grid-cols-4" style={{ marginBottom: 'var(--space-6, 24px)' }}>
        <MetricCard label="Projects" value="—" icon="folder" subtitle="Coming soon" />
        <MetricCard label="Team Members" value="—" icon="users" subtitle="Coming soon" />
        <MetricCard label="Compliance" value="—" icon="shield" subtitle="Coming soon" />
        <MetricCard label="AI Utilization" value="—" icon="cpu" subtitle="Coming soon" />
      </div>

      {/* Cross-functional summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-6, 24px)',
        marginBottom: 'var(--space-6, 24px)',
      }}>
        <div className="card card-elevated" style={{ padding: 'var(--space-4, 16px)' }}>
          <h3 style={{
            margin: '0 0 var(--space-4, 16px)',
            fontSize: 'var(--text-lg, 18px)',
            color: 'var(--text-primary, #e2e8f0)',
          }}>
            Functional Areas
          </h3>
          <p style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '14px', lineHeight: '1.5' }}>
            Cross-functional summary cards with key metrics per department will be
            available after functional area dashboards are implemented (WQ-087).
          </p>
        </div>
        <div className="card card-elevated" style={{ padding: 'var(--space-4, 16px)' }}>
          <h3 style={{
            margin: '0 0 var(--space-4, 16px)',
            fontSize: 'var(--text-lg, 18px)',
            color: 'var(--text-primary, #e2e8f0)',
          }}>
            Business Metrics
          </h3>
          <p style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '14px', lineHeight: '1.5' }}>
            Revenue, headcount, and project status KPIs will be available after
            the BI Command Center is implemented (Phase 4.8).
          </p>
        </div>
      </div>

      {/* Quick Actions scoped to tenant */}
      <div className="card card-elevated" style={{ padding: 'var(--space-4, 16px)' }}>
        <h3 style={{
          margin: '0 0 var(--space-4, 16px)',
          fontSize: 'var(--text-lg, 18px)',
          color: 'var(--text-primary, #e2e8f0)',
        }}>
          Quick Actions
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <QuickAction label="Settings" icon="settings" tab="tenant-settings" />
          <QuickAction label="Usage" icon="activity" tab="tenant-usage" />
        </div>
      </div>
    </ViewShell>
  );
};

// ---------------------------------------------------------------------------
// Platform ExecOverview Component (original — platform admin context)
// ---------------------------------------------------------------------------

const PlatformExecOverview: React.FC<ExecOverviewProps> = ({ context }) => {
  const { authFetch } = useAuth();

  const [tenantStats, setTenantStats] = useState<TenantStats | null>(null);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [killSwitch, setKillSwitch] = useState<KillSwitchStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const results = await Promise.allSettled([
        authFetch('/api/admin/tenants/stats').then(r => {
          if (!r.ok) throw new Error(`Tenant stats: ${r.status}`);
          return r.json();
        }),
        authFetch('/api/health').then(r => {
          if (!r.ok) throw new Error(`Health: ${r.status}`);
          return r.json();
        }),
        authFetch('/api/admin/ai/emergency/kill-switch').then(r => {
          if (!r.ok) throw new Error(`Kill switch: ${r.status}`);
          return r.json();
        }),
      ]);

      if (results[0].status === 'fulfilled') setTenantStats(results[0].value);
      if (results[1].status === 'fulfilled') setHealth(results[1].value);
      if (results[2].status === 'fulfilled') setKillSwitch(results[2].value);

      const allFailed = results.every(r => r.status === 'rejected');
      if (allFailed) {
        setError('Unable to load dashboard data. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Derived values
  const activeTenants = tenantStats?.by_status?.active ?? 0;
  const totalTenants = tenantStats?.total_tenants ?? 0;
  const healthStatus = health?.status ?? 'unknown';
  const aiStatus = killSwitch?.active ? 'HALTED' : 'Operational';
  const aiStatusLevel: 'good' | 'warn' | 'bad' = killSwitch?.active ? 'bad' : 'good';
  const platformVersion = health?.version ?? '—';

  const byIsolation = tenantStats?.by_isolation_level ?? {};

  return (
    <ViewShell
      context={context}
      title="Executive Overview"
      icon="layout-dashboard"
      subtitle="High-level operational dashboard"
      actions={
        <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>
          {resolveIcon('activity', 14)}
          <span style={{ marginLeft: '6px' }}>{loading ? 'Refreshing...' : 'Refresh'}</span>
        </button>
      }
    >
      {/* Error banner */}
      {error && !tenantStats && !health && (
        <div className="card card-elevated" style={{ textAlign: 'center', padding: 'var(--space-8, 32px)', marginBottom: 'var(--space-6, 24px)' }}>
          <h3 style={{ marginBottom: 'var(--space-2, 8px)' }}>Unable to load dashboard data</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4, 16px)' }}>{error}</p>
          <button className="btn btn-primary" onClick={fetchData}>Try Again</button>
        </div>
      )}

      {/* KPI Row */}
      {loading && !tenantStats ? (
        <KPISkeleton />
      ) : (
        <div className="grid grid-cols-4" style={{ marginBottom: 'var(--space-6, 24px)' }}>
          <MetricCard
            label="Active Tenants"
            value={`${activeTenants} / ${totalTenants}`}
            icon="users"
            status={activeTenants > 0 ? 'good' : 'warn'}
            subtitle={activeTenants === totalTenants ? 'All active' : `${totalTenants - activeTenants} inactive`}
          />
          <MetricCard
            label="System Health"
            value={healthStatus === 'healthy' ? 'Healthy' : healthStatus === 'degraded' ? 'Degraded' : '—'}
            icon="activity"
            status={healthStatus === 'healthy' ? 'good' : healthStatus === 'degraded' ? 'warn' : 'bad'}
            subtitle={health?.services ? `${Object.values(health.services).filter(s => s === 'healthy').length}/${Object.keys(health.services).length} services up` : undefined}
          />
          <MetricCard
            label="AI Status"
            value={aiStatus}
            icon="cpu"
            status={aiStatusLevel}
            subtitle={killSwitch?.active ? killSwitch.reason || 'Kill switch active' : 'All systems go'}
          />
          <MetricCard
            label="Platform Version"
            value={platformVersion}
            icon="package"
            status="good"
          />
        </div>
      )}

      {/* Two-column layout: Tenant Distribution + System Status */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-6, 24px)',
        marginBottom: 'var(--space-6, 24px)',
      }}>
        {/* Tenant Distribution */}
        <div className="card card-elevated" style={{ padding: 'var(--space-4, 16px)' }}>
          <h3 style={{
            margin: '0 0 var(--space-4, 16px)',
            fontSize: 'var(--text-lg, 18px)',
            color: 'var(--text-primary, #e2e8f0)',
          }}>
            Tenant Distribution
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {['row', 'schema', 'container'].map(level => {
              const count = byIsolation[level] ?? 0;
              const pct = totalTenants > 0 ? Math.round((count / totalTenants) * 100) : 0;
              return (
                <div key={level}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '14px', color: 'var(--text-primary, #e2e8f0)', textTransform: 'capitalize' }}>
                      {level}
                    </span>
                    <span style={{ fontSize: '14px', color: 'var(--text-secondary, #94a3b8)' }}>
                      {count} ({pct}%)
                    </span>
                  </div>
                  <div style={{
                    height: '8px',
                    background: 'var(--bg-secondary, rgba(255,255,255,0.05))',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: 'var(--accent-primary, #7c3aed)',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* System Status */}
        <div className="card card-elevated" style={{ padding: 'var(--space-4, 16px)' }}>
          <h3 style={{
            margin: '0 0 var(--space-4, 16px)',
            fontSize: 'var(--text-lg, 18px)',
            color: 'var(--text-primary, #e2e8f0)',
          }}>
            System Status
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {health ? (
              <>
                <StatusDot status="healthy" label="Database" />
                {health.services && Object.entries(health.services).map(([name, status]) => (
                  <StatusDot key={name} status={status} label={name.charAt(0).toUpperCase() + name.slice(1)} />
                ))}
                <StatusDot
                  status={killSwitch?.active ? 'unhealthy' : 'healthy'}
                  label="AI Laws"
                />
              </>
            ) : (
              <p style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '14px' }}>
                {loading ? 'Loading...' : 'Unable to load status'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card card-elevated" style={{ padding: 'var(--space-4, 16px)' }}>
        <h3 style={{
          margin: '0 0 var(--space-4, 16px)',
          fontSize: 'var(--text-lg, 18px)',
          color: 'var(--text-primary, #e2e8f0)',
        }}>
          Quick Actions
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <QuickAction label="Manage Tenants" icon="users" tab="tenants" />
          <QuickAction label="AI Configuration" icon="cpu" tab="llm" />
          <QuickAction label="View Logs" icon="activity" tab="logs" />
          <QuickAction label="Backups" icon="database" tab="platform-backups" />
        </div>
      </div>
    </ViewShell>
  );
};

// ---------------------------------------------------------------------------
// Context-aware router (WQ-086)
// ---------------------------------------------------------------------------

const ExecOverview: React.FC<ExecOverviewProps> = (props) => {
  const { isInTenantContext } = useAdminContext();

  if (isInTenantContext) {
    return <TenantExecView {...props} />;
  }

  return <PlatformExecOverview {...props} />;
};

export default ExecOverview;
